import React, { memo, MouseEvent, KeyboardEvent, useState } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import styles from "./machines-request-remote-machine-access-drawer-modal.module.scss";
import cn from "classnames";
import { Title } from "@src/components/atoms/title/title";
import { DrawerIconContainer } from "@src/components/atoms/drawer-icon-container/drawer-icon-container";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { ReactComponent as RequestRemoteUserIcon } from "@src/assets/svg/request-remote-access-user-icon.svg";
import IconBuilding from "@src/assets/svg/icon-building.svg";
import UserProfileIcon from "@src/assets/svg/user-profile-icon.svg";
import SourceIcon from "@src/assets/svg/source-icon.svg";
import RenameIcon from "@src/assets/svg/rename-blue-icon.svg";
import CloseIcon from "@src/assets/svg/close-icon.svg";
import questionMarkIcon from "@src/assets/svg/question-mark-full-gray-12.svg";

import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import {
  useMachinesRequestRemoteMachineAccessDrawerModalHook,
  TextsType,
  DurationOptionsType,
} from "./use-machines-request-remote-machine-access-drawer-hook";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { DrawerButtonWrapper } from "@src/components/molecules/drawer-button-wrapper/drawer-button-wrapper";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { useI18N } from "@src/language/language-context";
import { MachinesListSupportSessionsZitadelRoleTypes } from "@common/types/machines-list-types";
import whiteLockIcon from "@src/assets/svg/white-lock-icon.svg";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

const cssClassName = "machines-request-remote-machine-access-drawer-modal";
interface DurationPickerPropsTypes {
  durationValue: number;
  durationType: "minutes" | "hours";
  durationOptions: DurationOptionsType;
  texts: TextsType;
  selectMinutes: (e: MouseEvent | KeyboardEvent) => void;
  selectHours: (e: MouseEvent | KeyboardEvent) => void;
  selectDurationValue: (e: MouseEvent | KeyboardEvent, value: number) => void;
}

export const MachinesRequestRemoteMachineAccessDrawerModal = memo(() => {
  const {
    hasSTPermission,
    requestMessage,
    durationValue,
    durationType,
    sessionVisibility,
    IsRequestedStatus,
    durationOptions,
    selectedMachineInfo,
    handleCloseModal,
    selectMinutes,
    selectHours,
    selectDurationValue,
    selectSessionVisibility,
    onChangeRequestMessage,
    handleSTRequestAccessButtonClick,
    handleOTRequestedAccessButtonClick,
    handleConfirmCancel,
    conditionalStatusIcon,
    isEditingDuration,
    handleEditDuration,
    handleCloseEditDuration,
    sTRequestAccessButtonLoading,
    oTRequestAccessButtonLoading,
    confirmCancelLoading,
  } = useMachinesRequestRemoteMachineAccessDrawerModalHook();

  const i18n = useI18N();

  const MAX_MESSAGE_LENGTH = 500;
  const selectedUserType = selectedMachineInfo?.userType;
  const isRemoteActionsDisabled = useSSEUnavailable("service-vpn-manager");
  const [isSessionTypeInfoPopoverOpen, setIsSessionTypeInfoPopoverOpen] =
    useState(false);

  const texts = {
    title: i18n(
      `modules.machinesRequestRemoteMachineAccessDrawerModal.${
        hasSTPermission ? "STtitle" : "OTtitle"
      }`,
    ),
    requestDetails: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestDetails",
    ),
    sessionLength: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.sessionLength",
    ),
    sessionType: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.sessionType",
    ),
    privateSession: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.privateSession",
    ),
    publicSession: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.publicSession",
    ),
    privateSessionInfoPopoverText: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.privateSessionInfoPopoverText",
    ),
    publicSessionInfoPopoverText: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.publicSessionInfoPopoverText",
    ),
    sessionTypeInfoPopoverAltText: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.sessionTypeInfoPopoverAltText",
    ),
    requestedSessionLength: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestedSessionLength",
    ),
    requestMessage: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestMessage",
    ),
    requestMessagePlaceholder: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestMessagePlaceholder",
    ),
    requestAccessButton: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestAccessButton",
    ),
    cancelButton: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.cancelButton",
    ),
    minutes: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.minutes",
    ),
    hours: i18n("modules.machinesRequestRemoteMachineAccessDrawerModal.hours"),
    requestedBy: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.requestedBy",
    ),
    machine: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.machine",
    ),
    SourceIcon: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.SourceIcon",
    ),
    approveButton: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.approveButton",
    ),
    denyButton: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.denyButton",
    ),
    userProfileIcon: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.userProfileIcon",
    ),
    iconBuilding: i18n(
      "modules.machinesRequestRemoteMachineAccessDrawerModal.iconBuilding",
    ),
    reconnectingActionsUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
  };

  const remoteActionsDisabledTooltip = isRemoteActionsDisabled
    ? texts.reconnectingActionsUnavailableTooltip
    : "";
  const disabledActionStyle = isRemoteActionsDisabled
    ? { opacity: 0.4 }
    : undefined;

  let sessionTypeText = "-";
  switch (selectedMachineInfo?.visibility) {
    case "public":
      sessionTypeText = texts.publicSession;
      break;
    case "private":
      sessionTypeText = texts.privateSession;
      break;
  }

  return (
    <DrawerModal id="machinesRequestRemoteMachineAccessDrawerModal">
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={24}>
          <Title tag="h2" text={texts.title} />
          <DrawerIconContainer
            status={selectedMachineInfo?.status}
            icon={conditionalStatusIcon}
          />

          <div className={styles[`${cssClassName}__content-wrapper`]}>
            <FlexRowContainer horizontalGap={8}>
              <RequestRemoteUserIcon
                className={styles[`${cssClassName}__container-icon`]}
                role="img"
                aria-label={texts.requestDetails}
              />
              <p className={styles[`${cssClassName}__subtitle`]}>
                {texts.requestDetails}
              </p>
            </FlexRowContainer>

            <div className={styles[`${cssClassName}__details-box`]}>
              {IsRequestedStatus && (
                <>
                  <div className={styles[`${cssClassName}__session-label`]}>
                    {texts?.requestedBy}
                  </div>
                  <div className={styles[`${cssClassName}__detail-item`]}>
                    <img src={UserProfileIcon} alt={texts.userProfileIcon} />
                    <span>{selectedMachineInfo?.display_name}</span>
                  </div>
                </>
              )}

              <div className={styles[`${cssClassName}__detail-item`]}>
                <img src={IconBuilding} alt={texts.iconBuilding} />
                <span>{selectedMachineInfo?.companyName}</span>
              </div>

              <div className={styles[`${cssClassName}__session-section`]}>
                <div className={styles[`${cssClassName}__session-label`]}>
                  {texts?.machine}
                </div>
                <span className={styles[`${cssClassName}__detail-value`]}>
                  <img
                    src={SourceIcon}
                    alt={texts.SourceIcon}
                    className={styles[`${cssClassName}__source-icon`]}
                  />
                  <span className={styles[`${cssClassName}__info-text`]}>
                    {selectedMachineInfo?.sourceName ||
                      selectedMachineInfo?.sourceId}
                  </span>
                </span>
              </div>

              <div className={styles[`${cssClassName}__session-section`]}>
                <div
                  className={cn(
                    styles[`${cssClassName}__session-label`],
                    styles[`${cssClassName}__session-label--with-info`],
                  )}
                  onPointerLeave={() => {
                    setIsSessionTypeInfoPopoverOpen(false);
                  }}
                >
                  {texts.sessionType}
                  <img
                    src={questionMarkIcon}
                    alt={texts.sessionTypeInfoPopoverAltText}
                    className={
                      styles[`${cssClassName}__session-type-info-icon`]
                    }
                    onPointerEnter={() => {
                      setIsSessionTypeInfoPopoverOpen(true);
                    }}
                  />
                  {isSessionTypeInfoPopoverOpen && (
                    <div
                      className={
                        styles[`${cssClassName}__session-type-info-popover`]
                      }
                    >
                      <p
                        className={
                          styles[`${cssClassName}__session-type-info-title`]
                        }
                      >
                        {texts.privateSession}
                      </p>
                      <p
                        className={
                          styles[
                            `${cssClassName}__session-type-info-description`
                          ]
                        }
                      >
                        - {texts.privateSessionInfoPopoverText}
                      </p>
                      <p
                        className={
                          styles[`${cssClassName}__session-type-info-title`]
                        }
                      >
                        {texts.publicSession}
                      </p>
                      <p
                        className={
                          styles[
                            `${cssClassName}__session-type-info-description`
                          ]
                        }
                      >
                        - {texts.publicSessionInfoPopoverText}
                      </p>
                    </div>
                  )}
                </div>
                {hasSTPermission && !IsRequestedStatus ? (
                  <div className={styles[`${cssClassName}__session-options`]}>
                    <button
                      onClick={() => selectSessionVisibility("private")}
                      className={cn(
                        styles[`${cssClassName}__session-option-button`],
                        {
                          [styles[
                            `${cssClassName}__session-option-button--active`
                          ]]: sessionVisibility === "private",
                        },
                      )}
                    >
                      <img
                        className={styles[`${cssClassName}__privacy-icon`]}
                        alt={"privacy icon"}
                        src={whiteLockIcon}
                      />
                      {texts.privateSession}
                    </button>
                    <button
                      onClick={() => selectSessionVisibility("public")}
                      className={cn(
                        styles[`${cssClassName}__session-option-button`],
                        {
                          [styles[
                            `${cssClassName}__session-option-button--active`
                          ]]: sessionVisibility === "public",
                        },
                      )}
                    >
                      {texts.publicSession}
                    </button>
                  </div>
                ) : (
                  <span
                    className={styles[`${cssClassName}__session-item-text`]}
                  >
                    {sessionTypeText}
                  </span>
                )}
              </div>

              <div className={styles[`${cssClassName}__session-section`]}>
                <div>
                  <label
                    className={cn(
                      styles[`${cssClassName}__session-label`],
                      styles[`${cssClassName}__session-label--close-icon`],
                    )}
                  >
                    {IsRequestedStatus
                      ? texts?.requestedSessionLength
                      : texts?.sessionLength}
                    {isEditingDuration && (
                      <IconButton
                        fun={handleCloseEditDuration}
                        icon={CloseIcon}
                        variant="edit-and-close-icon"
                      />
                    )}
                  </label>
                </div>

                {isEditingDuration ? (
                  <DurationPicker
                    durationValue={durationValue}
                    durationType={durationType}
                    durationOptions={durationOptions}
                    texts={texts}
                    selectMinutes={selectMinutes}
                    selectHours={selectHours}
                    selectDurationValue={selectDurationValue}
                  />
                ) : IsRequestedStatus ? (
                  <span className={styles[`${cssClassName}__session-item`]}>
                    <span>
                      {`${durationValue} ${
                        durationType === "minutes"
                          ? texts?.minutes
                          : texts?.hours
                      }`}
                    </span>
                    {!hasSTPermission && (
                      <IconButton
                        fun={handleEditDuration}
                        icon={RenameIcon}
                        variant="edit-and-close-icon"
                      />
                    )}
                  </span>
                ) : (
                  <>
                    <DurationPicker
                      durationValue={durationValue}
                      durationType={durationType}
                      durationOptions={durationOptions}
                      texts={texts}
                      selectMinutes={selectMinutes}
                      selectHours={selectHours}
                      selectDurationValue={selectDurationValue}
                    />
                  </>
                )}
              </div>

              <div className={styles[`${cssClassName}__message-section`]}>
                <label
                  className={styles[`${cssClassName}__message-label`]}
                  htmlFor="requestMessage"
                >
                  {texts.requestMessage}
                </label>
                {IsRequestedStatus ? (
                  <span className={styles[`${cssClassName}__message-item`]}>
                    {selectedMachineInfo?.requestMessage || "-"}
                  </span>
                ) : (
                  <div
                    className={styles[`${cssClassName}__textarea-container`]}
                  >
                    <textarea
                      id="requestMessage"
                      value={requestMessage}
                      onChange={(e) => onChangeRequestMessage(e.target.value)}
                      placeholder={texts.requestMessagePlaceholder}
                      className={styles[`${cssClassName}__textarea`]}
                      rows={4}
                      maxLength={MAX_MESSAGE_LENGTH}
                    />
                    <div
                      className={cn(
                        styles[`${cssClassName}__textarea-charCount`],
                        requestMessage.length >= MAX_MESSAGE_LENGTH
                          ? styles[
                              `${cssClassName}__textarea-charCount--warning`
                            ]
                          : "",
                      )}
                    >
                      {requestMessage.length}/{MAX_MESSAGE_LENGTH}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DrawerButtonWrapper>
              {hasSTPermission ? (
                IsRequestedStatus ? (
                  <ButtonWithLoader
                    fun={(e) =>
                      handleConfirmCancel(
                        e,
                        selectedUserType as MachinesListSupportSessionsZitadelRoleTypes,
                      )
                    }
                    text={texts.cancelButton}
                    variant={["laser-orange", "full-width"]}
                    loading={confirmCancelLoading}
                    disabled={isRemoteActionsDisabled}
                    tooltip={remoteActionsDisabledTooltip}
                    style={disabledActionStyle}
                    spinnerVariant={["warning", "small"]}
                  />
                ) : (
                  <>
                    <ButtonWithLoader
                      fun={handleSTRequestAccessButtonClick}
                      text={texts.requestAccessButton}
                      loading={sTRequestAccessButtonLoading}
                      disabled={isRemoteActionsDisabled}
                      tooltip={remoteActionsDisabledTooltip}
                      style={disabledActionStyle}
                    />
                    <Button
                      fun={handleCloseModal}
                      text={texts.cancelButton}
                      variant={"secondary"}
                    />
                  </>
                )
              ) : (
                <>
                  <ButtonWithLoader
                    fun={handleOTRequestedAccessButtonClick}
                    text={texts.approveButton}
                    loading={oTRequestAccessButtonLoading}
                    disabled={isRemoteActionsDisabled}
                    tooltip={remoteActionsDisabledTooltip}
                    style={disabledActionStyle}
                  />
                  <ButtonWithLoader
                    fun={(e) =>
                      handleConfirmCancel(
                        e,
                        selectedUserType as MachinesListSupportSessionsZitadelRoleTypes,
                      )
                    }
                    text={texts.denyButton}
                    variant={"secondary"}
                    loading={confirmCancelLoading}
                    disabled={isRemoteActionsDisabled}
                    tooltip={remoteActionsDisabledTooltip}
                    style={disabledActionStyle}
                    spinnerVariant={["secondary", "small"]}
                  />
                </>
              )}
            </DrawerButtonWrapper>
          </div>
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
});

const DurationPicker = ({
  durationValue,
  durationType,
  durationOptions,
  texts,
  selectMinutes,
  selectHours,
  selectDurationValue,
}: DurationPickerPropsTypes) => (
  <div className={styles[`${cssClassName}__duration-container`]}>
    <div className={styles[`${cssClassName}__duration-tabs`]}>
      <button
        onClick={(e) => selectMinutes(e)}
        className={`${styles[`${cssClassName}__duration-tab-button`]} ${
          durationType === "minutes"
            ? styles[`${cssClassName}__duration-tab-button--active`]
            : ""
        }`}
      >
        {texts.minutes}
      </button>
      <button
        onClick={(e) => selectHours(e)}
        className={`${styles[`${cssClassName}__duration-tab-button`]} ${
          durationType === "hours"
            ? styles[`${cssClassName}__duration-tab-button--active`]
            : ""
        }`}
      >
        {texts?.hours}
      </button>
    </div>
    <div className={styles[`${cssClassName}__duration-options`]}>
      {durationOptions[durationType].map((value) => (
        <button
          key={value}
          onClick={(e) => selectDurationValue(e, value)}
          className={`${styles[`${cssClassName}__duration-option-button`]} ${
            durationValue === value
              ? styles[`${cssClassName}__duration-option-button--active`]
              : ""
          }`}
        >
          {value}
        </button>
      ))}
    </div>
  </div>
);

MachinesRequestRemoteMachineAccessDrawerModal.displayName =
  "MachinesRequestRemoteMachineAccessDrawerModal";
